import React from 'react';
import * as R from 'ramda';
import {
  FormikConfig,
  FormikValues,
  FormikErrors,
  useFormik,
  FormikTouched,
  FormikState,
  FieldInputProps,
  FieldMetaProps,
  FieldHelperProps,
} from 'formik';
import {UseMutationResult} from '@tanstack/react-query';
import {UnknownServerData} from '@practicegenius/http-clients-js/src/types';
import {ValidationException} from '@practicegenius/http-clients-js/src/exceptions';
import {voidOp} from '@practicegenius/pg-util/src/TypescriptUtils';
import {objectKeysToCamelCaseDeep} from '@practicegenius/pg-util/src/ObjectUtils';
import {pipe} from '@practicegenius/pg-util/src/FunctionUtils';
import {ZodError} from 'zod';

/** This is a copy-paste of Formik's definition of the return type of `useFormik`
 * It's copy-pasted because it's not a named type in their library, and we really need a
 *   direct definition of it so we can do our shenanigans with the generics.
 */
export type PgForm<Values> = {
  initialValues: Values;
  initialErrors: FormikErrors<unknown>;
  initialTouched: FormikTouched<unknown>;
  initialStatus: any;
  handleBlur: {
    (e: React.FocusEvent<any>): void;
    <T = any>(fieldOrEvent: T): T extends string ? (e: any) => void : void;
  };
  handleChange: {
    (e: React.ChangeEvent<any>): void;
    <T_1 = string | React.ChangeEvent<any>>(
      field: T_1
    ): T_1 extends React.ChangeEvent<any>
      ? void
      : (e: string | React.ChangeEvent<any>) => void;
  };
  handleReset: (e: any) => void;
  handleSubmit: (e?: React.FormEvent<HTMLFormElement> | undefined) => void;
  resetForm: (nextState?: Partial<FormikState<Values>> | undefined) => void;
  setErrors: (errors: FormikErrors<Values>) => void;
  setFormikState: (
    stateOrCb:
      | FormikState<Values>
      | ((state: FormikState<Values>) => FormikState<Values>)
  ) => void;
  setFieldTouched: (
    field: string,
    touched?: boolean,
    shouldValidate?: boolean | undefined
  ) => any;
  setFieldValue: (
    field: string,
    value: any,
    shouldValidate?: boolean | undefined
  ) => any;
  setFieldError: (field: string, value: string | undefined) => void;
  setStatus: (status: any) => void;
  setSubmitting: (isSubmitting: boolean) => void;
  setTouched: (
    touched: FormikTouched<Values>,
    shouldValidate?: boolean | undefined
  ) => any;
  setValues: (values: Values, shouldValidate?: boolean | undefined) => any;
  submitForm: () => Promise<any>;
  validateForm: (values?: Values) => Promise<FormikErrors<Values>>;
  validateField: (name: string) => Promise<void> | Promise<string | undefined>;
  isValid: boolean;
  dirty: boolean;
  unregisterField: (name: string) => void;
  registerField: (name: string, {validate}: any) => void;
  getFieldProps: <T = any>(
    nameOrOptions: any
  ) => FieldInputProps<T> & {error: string | undefined};
  getFieldMeta: (name: string) => FieldMetaProps<any>;
  getFieldHelpers: (name: string) => FieldHelperProps<any>;
  validateOnBlur: boolean;
  validateOnChange: boolean;
  validateOnMount: boolean;
  values: Values;
  errors: FormikErrors<Values>;
  touched: FormikTouched<Values>;
  isSubmitting: boolean;
  isValidating: boolean;
  status?: any;
  submitCount: number;
};

type Props<TFormValue, TResultData, TResultError, TMutationVariables> = {
  formikConfig: FormikConfig<TFormValue>;
  /**
   * The value returned by `useMutation()` from react-query
   */
  mutationResult: UseMutationResult<
    TResultData,
    TResultError,
    TMutationVariables
  >;
  /**
   * Called when the form submission is successful
   */
  onSubmitSuccess?: (
    data: TResultData,
    formik: PgForm<TFormValue>,
    mutationResult: UseMutationResult<
      TResultData,
      TResultError,
      TMutationVariables
    >
  ) => void;
  /**
   * Called when the form submission fails. This hook already integrates server-side validation errors
   * with Formik, so this only needs to do form-specific work.
   */
  onSubmitError?: (
    error: TResultError | null,
    formik: PgForm<TFormValue>,
    mutationResult: UseMutationResult<
      TResultData,
      TResultError,
      TMutationVariables
    >
  ) => void;

  transformServerErrors?: (
    errors: UnknownServerData
  ) => FormikErrors<TFormValue>;
};

export type PgFormFieldProps<T> = FieldInputProps<T> & {
  error: string | undefined;
};

/**
 * This hook is effectively a lightweight wrapper around formik
 * It has the exact same signature as the `useFormik`, except for a
 * slightly-enhanced `getFieldProps` helper that includes an error prop.
 * This builds in our desired UX of not showing any field-level errors
 * unless that field has been touched.
 */
export function usePgForm<TFormValue extends FormikValues>(
  config: FormikConfig<TFormValue>
): PgForm<TFormValue> {
  const formik = useFormik(config);

  // This enhances `formik.getFieldProps` by adding an 'error' prop
  // that is only defined if the field has been touched
  const getFieldProps = (field: keyof TFormValue) => {
    const {error, touched} = formik.getFieldMeta(field as string);

    return {
      error: touched ? error : undefined,
      ...formik.getFieldProps(field as string),
    } as PgFormFieldProps<typeof config['initialValues'][typeof field]>;
  };

  // We only want to consider errors on fields that have been touched
  const errorsOnTouchedFields = R.pick(
    R.keys(formik.touched),
    formik.errors
  ) as FormikErrors<TFormValue>;

  // setFieldValue doesn't set fields to Touched
  const setFieldValue: typeof formik.setFieldValue = async (...args) => {
    formik.setFieldValue(...args);
    formik.setFieldTouched(args[0], true, false);
  };

  return {
    ...formik,
    getFieldProps,
    errors: errorsOnTouchedFields,
    setFieldValue,
  };
}

/** Extract field-level validation errors from typical 422 response from api-core */
export function parseLaravelValidationErrorsForFormik<
  T = Record<string, string>
>(data: UnknownServerData): FormikErrors<T> {
  return pipe<FormikErrors<T>>(objectKeysToCamelCaseDeep, R.map(R.head))(data);
}

export function parseZodErrorsForFormik<T = Record<string, string>>(
  errors: ZodError['errors']
): FormikErrors<T> {
  return errors.reduce((acc, error) => {
    return R.assocPath(error.path, error.message, acc);
  }, {});
}

/**
 * Does the bulk of the work to integrate a formik instance and a mutation from react-query
 *
 * This hook is an extension of `usePgForm` that aims to be a more complete solution.
 * It accepts the result of a `useMutation` from react-query and sets up an effect that
 * simplifies getting it to work well with formik.
 */
export function usePgMutationForm<
  TFormValue extends FormikValues,
  TResultData,
  TResultError,
  TMutationVariables
>({
  formikConfig,
  mutationResult,
  onSubmitSuccess = voidOp,
  onSubmitError = voidOp,
  transformServerErrors,
}: Props<
  TFormValue,
  TResultData,
  TResultError,
  TMutationVariables
>): PgForm<TFormValue> {
  const formik = usePgForm(formikConfig);
  React.useEffect(() => {
    switch (mutationResult.status) {
      case 'success': {
        // Set the internal form status to success, which the discard dialog uses
        // to unblock navigation
        formik.setStatus('success');
        // Defer excuting the success callback to the next tick so the formik status
        // will definitely be updated.
        setTimeout(
          () => onSubmitSuccess(mutationResult.data, formik, mutationResult),
          0
        );
        return;
      }

      case 'error': {
        // Formik requires manually setting this
        formik.setSubmitting(false);
        const {error} = mutationResult;

        if (error instanceof ValidationException) {
          formik.setErrors(
            transformServerErrors
              ? transformServerErrors(error.errors)
              : parseLaravelValidationErrorsForFormik<TFormValue>(error.errors)
          );
        }

        return onSubmitError(error, formik, mutationResult);
      }

      default:
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mutationResult.status, formik.setErrors, formik.setStatus]);

  return formik;
}

/**
 * Wraps around getFieldProps for a simple text input.
 * The yielded value is updated immediately for maximum responsiveness, but the
 *  call to the formik onChange is debounced so we don't pay the high cost of
 *  full form validation with every input change.
 */
export const useDebouncedInputProps = (
  {value, onChange, ...rest}: PgFormFieldProps<string>,
  debounceTimeMs = 250
): PgFormFieldProps<string> => {
  /** We capture the incoming change events from the input so we can make the debounced call to onChange */
  const [changeEvent, setChangeEvent] =
    React.useState<React.ChangeEvent<HTMLInputElement> | null>(null);

  /** Debounce calls to `props.onChange` for the given debounceTime */
  React.useEffect(() => {
    const handler = setTimeout(() => {
      if (onChange && changeEvent) {
        onChange(changeEvent);
      }
    }, debounceTimeMs);

    return () => {
      clearTimeout(handler);
    };
  }, [changeEvent]);

  return {
    // Return the value from the last change event if we have it
    value: changeEvent?.target.value ?? value,
    onChange: setChangeEvent,
    ...rest,
  };
};
